import { useState } from "react";
import { toast } from "sonner";
import { Clock, Loader2, Mail, MapPin, MessageCircle, Phone, Send } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";
import { submitContact } from "@/lib/api";
import { btnPrimary, inputCls, labelCls, SITE } from "@/lib/site";

const SUBJECTS = ["Product enquiry", "Price query", "Service & support", "Other"];

export default function Contact() {
  usePageMeta(
    "Contact Us | Krithish Scientific Company",
    "Reach Krithish Scientific Company — address, phone numbers, email, WhatsApp and location map. We respond within one working day."
  );
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await submitContact(form);
      toast.success(`Message received — Ref ${res.ref}`, {
        description: "We'll get back to you within one working day.",
      });
      setForm({ name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact Sales & Support"
        title="Talk to the people who build it."
        sub="Questions on specifications, pricing, custom builds or service — reach us on any channel below."
      />

      <section className="bg-slate-50 py-20 lg:py-24" data-testid="contact-section">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-10 lg:grid-cols-2">
            {/* Info */}
            <div className="space-y-5">
              {[
                { icon: MapPin, title: "Works & Office", lines: SITE.addressLines },
                { icon: Phone, title: "Phone", lines: [SITE.phoneDisplay, SITE.phone2Display] },
                { icon: Mail, title: "Email", lines: [SITE.email] },
                { icon: Clock, title: "Working Hours", lines: [SITE.hours] },
              ].map((c, i) => (
                <FadeUp key={c.title} delay={i * 0.05}>
                  <div className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-6">
                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-safety">
                      <c.icon size={19} />
                    </span>
                    <div>
                      <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{c.title}</h3>
                      {c.lines.map((l) => (
                        <p key={l} className="mt-1 text-sm font-medium text-slate-900">{l}</p>
                      ))}
                    </div>
                  </div>
                </FadeUp>
              ))}

              <FadeUp delay={0.2}>
                <a
                  href={SITE.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-6 py-4 text-sm font-semibold text-white shadow-lg shadow-emerald-600/20 transition hover:bg-emerald-700"
                >
                  <MessageCircle size={18} /> Chat with us on WhatsApp
                </a>
              </FadeUp>

              <FadeUp delay={0.25}>
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="contact-map">
                  <iframe
                    title="Krithish Scientific Company location map"
                    src={SITE.mapEmbed}
                    loading="lazy"
                    className="h-64 w-full border-0"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </FadeUp>
            </div>

            {/* Form */}
            <FadeUp delay={0.1}>
              <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="contact-form">
                <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Send us a message</h2>
                <p className="mt-1.5 text-sm text-slate-500">We reply within one working day.</p>
                <div className="mt-6 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelCls}>Name *</label>
                      <input required data-testid="contact-name-input" className={inputCls} value={form.name} onChange={set("name")} />
                    </div>
                    <div>
                      <label className={labelCls}>Phone *</label>
                      <input required data-testid="contact-phone-input" className={inputCls} placeholder="+91…" value={form.phone} onChange={set("phone")} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Email *</label>
                    <input type="email" required data-testid="contact-email-input" className={inputCls} value={form.email} onChange={set("email")} />
                  </div>
                  <div>
                    <label className={labelCls}>Subject</label>
                    <select data-testid="contact-subject-select" className={inputCls} value={form.subject} onChange={set("subject")}>
                      {SUBJECTS.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Message *</label>
                    <textarea rows={5} required data-testid="contact-message-input" className={inputCls} value={form.message} onChange={set("message")} />
                  </div>
                  <button data-testid="contact-form-submit-btn" className={`${btnPrimary} w-full`} disabled={busy}>
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    {busy ? "Sending…" : "Send Message"}
                  </button>
                </div>
              </form>
            </FadeUp>
          </div>
        </div>
      </section>
    </>
  );
}
